import { useEffect, useRef } from 'react'
import { checkInputErrorFileMaxSize, useFile, useViewport } from '@chilecompra/react-kit'
import { Button, Typography } from '@chilecompra/react-kit/components'
import PropTypes from 'prop-types'

import {
  ACCEPTED_DOCUMENT_TYPES,
  ACCEPTED_FILE_EXTENSIONS,
  MAX_MB
} from '../../../../config/settings/constants'
import { checkInputErrorFileExtension } from '../../../../modules/utils/checkers'
import { formatFileSize } from '../../../../utils/formatFileSize'
import { MAX_FILES_PER_ATTACHMENT } from '../../Cfmp.constants'
import {
  FileCard,
  FileIcon,
  FileInfo,
  FileUploadIcon,
  FileUploadInfo,
  FileUploadWrapper,
  HiddenInput,
  RemoveIcon
} from '../../Cfmp.styles'

/**
 * The CfmpFormAttachmentsCategory component.
 */
export const CfmpFormAttachmentsCategory = ({
  category,
  error = '',
  files = [],
  isDisabled = false,
  onAdd,
  onRemove
}) => {
  const inputRef = useRef(null)
  const { size } = useViewport()

  const {
    error: fileError,
    onChange: handleFileChange,
    onReset: handleFileReset,
    value: fileValue
  } = useFile({
    errorCallbacks: [
      checkInputErrorFileMaxSize(MAX_MB),
      checkInputErrorFileExtension(ACCEPTED_FILE_EXTENSIONS)
    ]
  })

  const errorText = fileError || error
  const isMaxReached = files.length >= MAX_FILES_PER_ATTACHMENT

  const handleButtonClick = () => {
    if (inputRef.current) inputRef.current.click()
  }

  const handleInputChange = event => {
    const selectedFiles = event.target.files

    if (selectedFiles && selectedFiles.length > 0) handleFileChange(selectedFiles)

    event.target.value = ''
  }

  useEffect(() => {
    if (fileError === '' && fileValue) {
      onAdd(category.id, fileValue)
      handleFileReset()
    }
  }, [category.id, fileError, fileValue, onAdd, handleFileReset])

  return (
    <FileUploadWrapper direction="column" spacing={1.5}>
      <FileUploadInfo direction="column" spacing={0.5}>
        <Typography fontWeight="bold" variant="body2">
          {category.title}
        </Typography>

        <Typography color="black3" variant="body2">
          {category.description}
        </Typography>
      </FileUploadInfo>

      <HiddenInput
        accept={ACCEPTED_DOCUMENT_TYPES}
        onChange={handleInputChange}
        ref={inputRef}
        type="file"
      />

      <Button
        color="primary"
        disabled={isDisabled || isMaxReached}
        onClick={handleButtonClick}
        variant="outlined"
        width={size.isTiny ? '100%' : '250px'}
      >
        Subir archivo ({files.length}/{MAX_FILES_PER_ATTACHMENT}) <FileUploadIcon />
      </Button>

      {errorText && (
        <Typography color="error" variant="subtitle">
          {errorText}
        </Typography>
      )}

      {isMaxReached && (
        <Typography color="black3" variant="caption">
          Alcanzaste el máximo de {MAX_FILES_PER_ATTACHMENT} archivos para esta sección.
        </Typography>
      )}

      {files.map(file => (
        <FileCard key={file.id}>
          <FileIcon />

          <FileInfo>
            <Typography className="file__name" variant="body2">
              {file.fileName}
            </Typography>

            <Typography className="file__size" variant="caption">
              {formatFileSize(file.file?.size)}
            </Typography>
          </FileInfo>

          <RemoveIcon disabled={isDisabled} onClick={onRemove(file.id)} />
        </FileCard>
      ))}
    </FileUploadWrapper>
  )
}

CfmpFormAttachmentsCategory.propTypes = {
  category: PropTypes.shape({
    description: PropTypes.string,
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  }).isRequired,
  error: PropTypes.string,
  files: PropTypes.array,
  isDisabled: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired
}
